import { useState } from "react";
import { Navigate, useParams } from "react-router-dom";
import { Button } from "../design-system";
import { friendlyError, invalidateQueries } from "../analytics";
import { trackButtonPressed } from "../analytics/tracking";
import { useAction, useIdentityClient, useInvite } from "../identity";
import { BootScreen, LoadingDots } from "../components/layout/DashboardBoot";

/* The explicit accept-invitation step (PLAT-489). Invite emails link to
 * /auth?invitation_id=… (which redirects to /login); once the viewer is
 * authenticated, LoginRoute forwards here to /invite/:invitationId. We resolve
 * the invite to the inviting org, show a
 * "Join {org}" confirmation, and on accept call the dedicated endpoint
 * (POST .../invite/{id}/accept) — which works whether or not the id rode the
 * sign-in exchange, unlike the legacy implicit attach.
 *
 * On success we drop to "/" (WorkspaceRedirect resolves the now-accessible
 * workspace) rather than guessing a /w/:id, since the accept only returns the
 * org, not a workspace. */
export default function AcceptInvitationPage() {
  const { invitationId = "" } = useParams<{ invitationId: string }>();
  const client = useIdentityClient();
  const invite = useInvite(invitationId);
  const [accepted, setAccepted] = useState(false);

  const accept = useAction(async () => {
    await client.acceptInvitation(invitationId);
    // The viewer's workspace list now includes the joined org; evict so the
    // post-accept redirect resolves against fresh data.
    invalidateQueries("identity:workspaces");
  }, "acceptInvitation");

  if (accepted) return <Navigate to="/" replace />;

  // The invite couldn't be resolved: unknown id, already consumed, or not for
  // this account. The message from the service explains which.
  if (invite.error) {
    return (
      <BootScreen>
        <div className="flex max-w-sm flex-col items-center gap-2 text-center">
          <p className="text-body font-medium text-fg-1">
            This invitation isn’t available
          </p>
          <p className="text-body-sm text-fg-2">
            {friendlyError(invite.error).message} Ask your organization admin to
            resend it.
          </p>
        </div>
      </BootScreen>
    );
  }

  if (invite.initialLoading || !invite.data) {
    return (
      <BootScreen>
        <LoadingDots />
      </BootScreen>
    );
  }

  const orgName = invite.data.organization.name;

  return (
    <BootScreen>
      <div className="flex max-w-sm flex-col items-center gap-4 text-center">
        <div className="flex flex-col gap-1">
          <p className="text-h4 text-fg-1">Join {orgName}</p>
          <p className="text-body-sm text-fg-2">
            You’ve been invited to {orgName} on the AI21 Coding Gateway. Accept
            to join and see its dashboards.
          </p>
        </div>

        {accept.error != null && (
          <p className="text-body-sm text-error-fg">
            {friendlyError(accept.error).message}
          </p>
        )}

        <Button
          variant="brand"
          disabled={accept.busy}
          onClick={async () => {
            trackButtonPressed("Join organization", "invitation");
            if (await accept.run()) setAccepted(true);
          }}
        >
          {accept.busy ? "Joining…" : `Join ${orgName}`}
        </Button>
      </div>
    </BootScreen>
  );
}
